/** Direction encoding: 0 none, 1 up, 2 right, 3 down, 4 left. */
export type Dir = 0 | 1 | 2 | 3 | 4;
export const DIR_NONE: Dir = 0;
export const DIR_UP: Dir = 1;
export const DIR_RIGHT: Dir = 2;
export const DIR_DOWN: Dir = 3;
export const DIR_LEFT: Dir = 4;
export const DX = [0, 0, 1, 0, -1] as const;
export const DY = [0, -1, 0, 1, 0] as const;
export const OPPOSITE: readonly Dir[] = [0, 3, 4, 1, 2];
export function isHorizontal(d: Dir): boolean {
  return d === 2 || d === 4;
}

/**
 * The ten per-level difficulty parameters (brief §3 "Difficulty vector").
 * Every board JSON must supply all ten; the sim never derives them from L.
 */
export interface LevelParams {
  /** Ticks between creature spawns. Brief baseline: 45 − 2L. */
  spawnGapTicks: number;
  /** Creatures released over the whole level. Brief baseline: L + 5. */
  totalCreatures: number;
  /** Max creatures alive at once. Brief baseline: 3 (L1), 4 (L2–7), 5 (L8+). */
  onScreenCap: number;
  /** Crawler mutates into a Borer when its frustration counter exceeds this. Brief baseline: 10 − L. */
  mutateThreshold: number;
  /** Borer reverts to Crawler when its counter exceeds this. Brief baseline: 30 + 2L. */
  mutateDurationTicks: number;
  /** Creature speed as ticks per cell (lower = faster). */
  creatureTicksPerCell: number;
  /** "Turn frequency": probability at each cell boundary that a Crawler swaps its 1st and 3rd ranked direction. Brief: >0 only on L<6. */
  turnSwapChance: number;
  /** Ticks a broken sack's gold stays on the field. Brief baseline: 150 − 10L. */
  goldTicks: number;
  /**
   * WP6: lantern shots banked at level start (Systems Brief §2.1; MVP v0.2 §3.2). Replaces the timed
   * flame recharge, which is gone: 8 collected gems fill the lantern for one shot. Default 1.
   */
  lanternStartCharges: number;
  /**
   * WP6 (PM 2026-09-07): gems collected while the lantern is full that bank one charge. Default 4,
   * so a Beam from an empty lantern is 8 + 5×4 = 28 gems (about one per normal board). In the
   * difficulty vector (Level Generator Spec §3 "Lantern").
   */
  gemsPerCharge: number;
  /** Bonus-mode duration in ticks. Brief baseline: 250 − 20L. */
  bonusTicks: number;
  /**
   * Rule param (not one of the ten difficulty params): ticks a falling sack holds above the
   * player's cell before a >1-row crush. Defaults to DEFAULTS.sackShadowTicks; 0 disables.
   */
  sackShadowTicks: number;
  /** Rule param: a Crawler whose preferred direction is blocked by dirt also gains frustration. Default true. */
  frustrationCountsDirt: boolean;
  /** Rule param: player speed in ticks per cell. Defaults to DEFAULTS.playerTicksPerCellH/V (5 / 6). */
  playerSpeedTicksPerCell: { h: number; v: number };
}

/** Board JSON params: rule params and the lantern params may be omitted (shipped boards set them explicitly). */
export type LevelParamsInput = Omit<LevelParams, 'sackShadowTicks' | 'frustrationCountsDirt' | 'playerSpeedTicksPerCell' | 'lanternStartCharges' | 'gemsPerCharge'> &
  Partial<Pick<LevelParams, 'sackShadowTicks' | 'frustrationCountsDirt' | 'playerSpeedTicksPerCell' | 'lanternStartCharges' | 'gemsPerCharge'>> & { flameRechargeTicks?: number };

export interface BoardCell {
  x: number;
  y: number;
}

/** Gem types by index (colour order of gem.0–gem.4 in the sheet and palette). */
export const GEM_NAMES = ['ruby', 'emerald', 'sapphire', 'amethyst', 'topaz'] as const;
export type GemName = (typeof GEM_NAMES)[number];
export const GEM_TYPE_COUNT = 5;
/** Default gem type of a '*' cell: the same position hash the renderer used before gem types existed. */
export function defaultGemType(x: number, y: number): number {
  return (x * 7 + y * 3) % GEM_TYPE_COUNT;
}

// ---------------------------------------------------------------------------
// Objectives (Systems Brief §1, WP5). All board fields are optional; boards without goals keep the
// legacy end rule (all gems or all creatures).

export type GoalType = 'collect' | 'crush' | 'break' | 'survive' | 'clear' | 'rescue';
export type ObstacleType = 'pillar' | 'boulder' | 'hardDirt' | 'lockedGem' | 'gemSpring' | 'nest';

export interface GoalData {
  id?: string;
  type: GoalType;
  /** Phase index this goal belongs to (default 0). Counters start when the phase begins. */
  phase?: number;
  /** collect: gem type name or index; omit for any gem. */
  gemType?: GemName | number;
  /** collect / crush / break: how many. */
  count?: number;
  /** survive: seconds (converted to ticks at 12.5 Hz). */
  seconds?: number;
  /** clear: key into `regions`. */
  regionId?: string;
  /** rescue: the buried cell to reach. */
  cell?: [number, number];
}

export interface PhaseData {
  name?: string;
  /** Obstacle / pickup ids activated when this phase begins (in addition to their own `phase` field). */
  activations?: string[];
}

export interface ObstacleData {
  id?: string;
  type: ObstacleType;
  cell: [number, number];
  /** Phase at which the obstacle activates: pillar rises, nest opens, spring starts. Default 0 (present from the start). */
  phase?: number;
  /** pillar: flame hits to break (2–4). */
  hits?: number;
  /** lockedGem: digging this cell (or crushing a Crawler on it) unlocks the gem. */
  switchCell?: [number, number];
  /** gemSpring: ticks between respawns while a pillar of its phase stands. */
  rateTicks?: number;
  /** gemSpring / lockedGem: gem type. */
  gemType?: GemName | number;
  /** nest: spawn gap override for creatures released from the nest. */
  spawnGap?: number;
}

export type RegionData = { rect: [number, number, number, number] } | { cells: [number, number][] };

export interface PickupData {
  type: string;
  cell: [number, number];
  /** Phase at which the pickup appears (default 0). */
  phase?: number;
}

/** Board JSON schema (see README). */
export interface BoardData {
  id: string;
  name: string;
  teaches: string;
  width: number;
  height: number;
  /** Row strings, one char per cell: '#' dirt, '.' tunnel, '*' gem, 'O' sack, 'P' player start, 'S' creature spawn. */
  rows: string[];
  /** The ten difficulty params are required; rule params and lanternStartCharges default (see DEFAULTS). */
  params: LevelParamsInput;
  /** Labelled hard level (Bible §4 TEMPER tag; Brief P5). */
  hard?: boolean;
  /** Labelled super-hard level (MVP v0.2 §3.1 slot 9). */
  superHard?: boolean;
  /** Part of the Gauntlet rotation (goal-less boards only; WP11 refines). */
  gauntlet?: boolean;
  goals?: GoalData[];
  phases?: PhaseData[];
  obstacles?: ObstacleData[];
  regions?: Record<string, RegionData>;
  pickups?: PickupData[];
}

export interface Goal {
  id: string;
  type: GoalType;
  phase: number;
  /** collect: gem type index, or -1 for any. */
  gemType: number;
  /** Target count (collect/crush/break: items; survive: ticks; clear: dirt cells; rescue: 1). */
  target: number;
  progress: number;
  done: boolean;
  regionId: string;
  cell: BoardCell | null;
}

export interface Obstacle {
  id: string;
  type: ObstacleType;
  x: number;
  y: number;
  phase: number;
  /** pillar raised / nest open / spring started / pickup shown. hardDirt, lockedGem, boulder are active from the start. */
  active: boolean;
  /** pillar: broken; hardDirt: cleared; lockedGem: collected. */
  done: boolean;
  hits: number;
  hitsLeft: number;
  /** hardDirt: first dig landed. */
  cracked: boolean;
  /** lockedGem: case opened. */
  unlocked: boolean;
  switchCell: BoardCell | null;
  rateTicks: number;
  timer: number;
  gemType: number;
  spawnGap: number;
}

export interface Pickup {
  type: string;
  x: number;
  y: number;
  phase: number;
  active: boolean;
  collected: boolean;
}

export interface Objectives {
  goals: Goal[];
  phaseCount: number;
  phase: number;
  phases: PhaseData[];
  obstacles: Obstacle[];
  regions: Record<string, BoardCell[]>;
  pickups: Pickup[];
  /** Inert inventory of collected pickups (WP6 spends them). */
  inventory: Record<string, number>;
  /** Nest spawns alternate with the main spawn; this flips each spawn while a nest is open. */
  nestTurn: boolean;
}

export interface Board {
  id: string;
  name: string;
  teaches: string;
  width: number;
  height: number;
  dug: number[]; // 1 = tunnel, 0 = dirt
  gems: number[]; // 1 = gem present
  /** Gem type per cell (0–4); meaningful where gems[i] = 1. */
  gemTypes: number[];
  sacks: BoardCell[];
  /** Boulders sit like sacks but never break into gold. */
  boulders: BoardCell[];
  playerStart: BoardCell;
  spawn: BoardCell;
  params: LevelParams;
  hard: boolean;
  superHard: boolean;
  gauntlet: boolean;
  objectives: Objectives;
}

export interface Player {
  x: number;
  y: number;
  dir: Dir; // current movement direction (0 = stopped)
  facing: Dir; // last non-zero direction, used for flame
  moving: boolean;
  prog: number; // ticks into the current cell move
  ticks: number; // ticks the current move takes
  queued: Dir; // buffered turn, persists until it can execute
  /** Stop requested mid-move: finish the current cell, then halt (WP6 fix; a stop used to snap Pip back). */
  stopping: boolean;
  queuedBoundaries: number; // boundaries passed while queued and unusable
  alive: boolean;
  deathTimer: number;
  deathCause: 'creature' | 'crushed' | '';
  invincible: number;
  /** Ticks held at a boundary while cracking hard dirt (WP5). */
  digHold: number;
  /** Shield bubble ticks remaining (0 = none); absorbs one hit then pops (WP6). */
  shield: number;
  /** Turbo ticks remaining: 2× speed, hard dirt in one dig, Crawlers avoid Pip (WP6). */
  turbo: number;
}

/** Power-ups (Systems Brief §2.2). 'fruit' is the existing bonus mode, in-level only. */
export type KitKind = 'shield' | 'turbo' | 'beam' | 'fruit';
export const KIT_KINDS: readonly KitKind[] = ['shield', 'turbo', 'beam', 'fruit'];

/** The lantern is the fire economy (Systems Brief §2.1): gems fill it, a full lantern is one shot, extra gems bank charges, five charges are a Beam. */
export interface Lantern {
  fill: number;
  charges: number;
  /** Gems banked toward the next charge (params.gemsPerCharge make one). */
  chargeFill: number;
}

export interface Beam {
  x: number;
  y: number;
  dir: Dir;
  /** Render ticks remaining. */
  ticks: number;
  /** Cells the beam swept (for the renderer). */
  length: number;
}

export type CreatureKind = 'crawler' | 'borer';

export interface Creature {
  id: number;
  x: number;
  y: number;
  dir: Dir;
  moving: boolean;
  prog: number;
  ticks: number;
  kind: CreatureKind;
  frustration: number;
  turnPenalty: number;
}

export type SackState = 'rest' | 'wobble' | 'fall';

export interface Sack {
  id: number;
  x: number;
  y: number;
  /** 'boulder' never breaks into gold and cannot be eaten by a Borer. */
  kind: 'sack' | 'boulder';
  state: SackState;
  wobble: number;
  fallProg: number;
  rowsFallen: number;
  /** Render-only slide animation after a horizontal push. */
  slideFromX: number;
  slideT: number;
  /** Remaining "sack shadow" hold ticks while the sack hangs above the player's cell. */
  shadow: number;
  /** The hold was already used for the row about to be entered. */
  shadowSpent: boolean;
}

export interface Gold {
  x: number;
  y: number;
  timer: number;
}

export interface Flame {
  x: number;
  y: number;
  dir: Dir;
  prog: number;
}

export type Phase = 'playing' | 'dying' | 'levelClear' | 'gameOver' | 'won' | 'timeUp';

export type SimMode = 'campaign' | 'gauntlet';

export interface SimConfig {
  mode: SimMode;
  seed: number;
  /** Boards played in order. Gauntlet cycles through them. */
  boards: Board[];
  /** Gauntlet: hard stop after this many ticks. */
  timeLimitTicks?: number;
  infiniteLives?: boolean;
  /** Optional override for player speed (ticks per cell). Recorded so replays stay deterministic. */
  playerTicksPerCellH?: number;
  playerTicksPerCellV?: number;
  /** Pre-level picker choices (Systems Brief §2.2), applied at the first level of the run. Recorded. */
  picks?: KitKind[];
}

export interface SimState {
  tick: number;
  rng: number;
  phase: Phase;
  phaseTimer: number;
  /** 1-based level number (display + telemetry only; params come from board data). */
  level: number;
  boardIndex: number;
  boardId: string;
  width: number;
  height: number;
  dug: number[];
  gems: number[];
  gemTypes: number[];
  /** 0 none, 1 intact hard dirt, 2 cracked (WP5). */
  hardDirt: number[];
  gemsRemaining: number;
  gemsTotal: number;
  params: LevelParams;
  objectives: Objectives;
  player: Player;
  playerStart: BoardCell;
  spawn: BoardCell;
  creatures: Creature[];
  nextCreatureId: number;
  sacks: Sack[];
  nextSackId: number;
  golds: Gold[];
  flame: Flame | null;
  score: number;
  lives: number; // spare lives
  nextLifeAt: number;
  chainCount: number;
  chainTimer: number;
  spawnTimer: number;
  spawned: number;
  killed: number;
  total: number;
  bonusItem: BoardCell | null;
  bonusTimer: number;
  bonusEatValue: number;
  bonusActive: boolean;
  levelsCleared: number;
  playerTicksH: number;
  playerTicksV: number;
  lantern: Lantern;
  beam: Beam | null;
}

export interface InputFrame {
  /** New direction requested this tick (swipe / key), 0 for none. */
  dir: Dir;
  /** Tap-on-player: stop at next boundary. */
  stop: boolean;
  /** Fire button pressed this tick. */
  fire: boolean;
  /** Any pointerdown/touchstart this tick. Only meaningful during the death pause (skips it). */
  skip?: boolean;
  /** Beam requested this tick (lantern held 0.5 s). */
  beam?: boolean;
}

export type SimEvent =
  | { type: 'wobbleStart'; x: number; y: number }
  | { type: 'sackFall'; x: number; y: number }
  | { type: 'sackLand'; x: number; y: number; rows: number }
  | { type: 'sackBreak'; x: number; y: number }
  | { type: 'sackShadow'; x: number; y: number; ticks: number }
  | { type: 'sackPush'; x: number; y: number; dx: number; count: number }
  | { type: 'pushBlocked'; x: number; y: number }
  | { type: 'gem'; x: number; y: number; chain: number }
  | { type: 'octave' }
  | { type: 'gold'; x: number; y: number }
  | { type: 'dig'; x: number; y: number; dir: Dir }
  | { type: 'fire'; x: number; y: number; dir: Dir }
  | { type: 'flameEnd'; x: number; y: number; dir: Dir }
  /** The lantern just filled: a shot is ready. */
  | { type: 'flameReady' }
  | { type: 'creatureTurn'; id: number; dir: Dir }
  /** Fire pressed with nothing to fire: 'dry' (empty lantern, no charges: click + shake, no cooldown), a flame in flight, or no facing. */
  | { type: 'fireBlocked'; reason: 'dry' | 'inFlight' | 'noFacing' }
  | { type: 'creatureKill'; id: number; by: 'flame' | 'sack' | 'eaten' | 'beam' | 'shield'; points: number }
  | { type: 'creatureSpawn'; id: number; last: boolean; x: number; y: number }
  | { type: 'mutate'; id: number; kind: CreatureKind }
  | { type: 'bonusAppear'; x: number; y: number }
  | { type: 'bonusStart' }
  | { type: 'bonusEnd' }
  | { type: 'playerDeath'; cause: 'creature' | 'crushed' }
  | { type: 'respawn' }
  | { type: 'extraLife' }
  | { type: 'turnQueued'; dir: Dir }
  | { type: 'turnExecuted'; dir: Dir; boundariesWaited: number }
  | { type: 'turnMissed'; dir: Dir; reason: 'replaced' | 'stopped' | 'death' | 'levelEnd'; boundariesWaited: number }
  | { type: 'stop' }
  | { type: 'levelStart'; level: number; boardId: string }
  | { type: 'levelClear'; level: number; boardId: string }
  | { type: 'gameOver' }
  | { type: 'won' }
  | { type: 'timeUp' }
  // --- objectives (WP5)
  | { type: 'goalProgress'; id: string; goal: GoalType; progress: number; target: number }
  | { type: 'goalComplete'; id: string; goal: GoalType }
  | { type: 'phaseChanged'; phase: number; final: boolean }
  | { type: 'obstacleRaised'; id: string; kind: ObstacleType; x: number; y: number }
  | { type: 'obstacleHit'; id: string; kind: ObstacleType; x: number; y: number; hitsLeft: number }
  | { type: 'obstacleBroken'; id: string; kind: ObstacleType; x: number; y: number; by: 'flame' | 'sack' | 'dig' | 'borer' }
  | { type: 'springSpawn'; id: string; x: number; y: number; gemType: number }
  | { type: 'nestOpened'; id: string; x: number; y: number }
  | { type: 'pickupCollected'; kind: string; x: number; y: number }
  | { type: 'lockUnlocked'; id: string; x: number; y: number; by: 'dig' | 'crush' }
  // --- kit and lantern (WP6)
  | { type: 'lanternFill'; fill: number; full: boolean }
  /** A goal can no longer complete (B1 backstop): the level restarts at the cost of a life. */
  | { type: 'goalImpossible'; goalId: string; goalType: Goal['type'] }
  | { type: 'chargeBanked'; charges: number }
  | { type: 'beam'; x: number; y: number; dir: Dir; length: number; kills: number }
  | { type: 'beamBlocked'; charges: number }
  | { type: 'kitApplied'; kind: KitKind; source: 'pick' | 'pickup' }
  | { type: 'shieldPop'; cause: 'creature' | 'crushed' | 'timeout' }
  | { type: 'turboEnd' };
